import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { usePublicLanguage } from '../../contexts/PublicLanguageContext';
import PublicFooter from '../../components/PublicFooter';
import {
  Database,
  FileText,
  Users,
  Calendar,
  Brain,
  Trello,
  Zap,
  ArrowRight,
  Target,
  Clock,
  Shield,
  Globe,
  StickyNote
} from 'lucide-react';
import brandLogo from '../../assets/brand/logo.png';

const NEEDS_ARROW_CURVE_DIRECTION = [-1, 1, -1, 1, 1, 0, -1];

const FeaturesPage = () => {
  const { t, currentLanguage } = usePublicLanguage();

  const features = [
    {
      icon: Database,
      title: t('features.database.title'),
      description: t('features.database.description'),
      color: 'from-blue-500 to-cyan-500',
      benefits: [
        t('features.database.benefits.import'),
        t('features.database.benefits.organization'),
        t('features.database.benefits.search')
      ]
    },
    {
      icon: FileText,
      title: t('features.offerManagement.title'),
      description: t('features.offerManagement.description'),
      color: 'from-purple-500 to-pink-500',
      benefits: [
        t('features.offerManagement.benefits.creation'),
        t('features.offerManagement.benefits.publication'),
        t('features.offerManagement.benefits.tracking')
      ]
    },
    {
      icon: Users,
      title: t('features.applicationCentralization.title'),
      description: t('features.applicationCentralization.description'),
      color: 'from-green-500 to-emerald-500',
      benefits: [
        t('features.applicationCentralization.benefits.collection'),
        t('features.applicationCentralization.benefits.sorting'),
        t('features.applicationCentralization.benefits.notifications')
      ]
    },
    {
      icon: Trello,
      title: t('features.recruitmentTracking.title'),
      description: t('features.recruitmentTracking.description'),
      color: 'from-orange-500 to-red-500',
      benefits: [
        t('features.recruitmentTracking.benefits.visual'),
        t('features.recruitmentTracking.benefits.collaboration'),
        t('features.recruitmentTracking.benefits.tracking')
      ]
    },
    {
      icon: StickyNote,
      title: t('features.notesManagement.title'),
      description: t('features.notesManagement.description'),
      color: 'from-yellow-500 to-orange-500',
      benefits: [
        t('features.notesManagement.benefits.shared'),
        t('features.notesManagement.benefits.realtime'),
        t('features.notesManagement.benefits.history')
      ]
    },
    {
      icon: Brain,
      title: t('features.aiMatching.title'),
      description: t('features.aiMatching.description'),
      color: 'from-indigo-500 to-purple-500',
      benefits: [
        t('features.aiMatching.benefits.automatic'),
        t('features.aiMatching.benefits.intelligent'),
        t('features.aiMatching.benefits.decision')
      ]
    },
    {
      icon: Calendar,
      title: t('features.appointmentBooking.title'),
      description: t('features.appointmentBooking.description'),
      color: 'from-teal-500 to-green-500',
      benefits: [
        t('features.appointmentBooking.benefits.calendar'),
        t('features.appointmentBooking.benefits.automation'),
        t('features.appointmentBooking.benefits.videoIntegration')
      ]
    }
  ];

  const benefits = [
    {
      icon: Clock,
      title: t('features.benefits.timeGain.title'),
      description: t('features.benefits.timeGain.description')
    },
    {
      icon: Target,
      title: t('features.benefits.precision.title'),
      description: t('features.benefits.precision.description')
    },
    {
      icon: Shield,
      title: t('features.benefits.security.title'),
      description: t('features.benefits.security.description')
    },
    {
      icon: Globe,
      title: t('features.benefits.accessibility.title'),
      description: t('features.benefits.accessibility.description')
    }
  ];

  const stats = [
    { number: '90%', label: t('features.stats.timeReduction') },
    { number: '75%', label: t('features.stats.precisionImprovement') },
    { number: '60%', label: t('features.stats.processAcceleration') },
    { number: '95%', label: t('features.stats.userSatisfaction') }
  ];

  const processSteps = [
    { title: t('features.processSteps.publication.title'), description: t('features.processSteps.publication.description') },
    { title: t('features.processSteps.collection.title'), description: t('features.processSteps.collection.description') },
    { title: t('features.processSteps.analysis.title'), description: t('features.processSteps.analysis.description') },
    { title: t('features.processSteps.selection.title'), description: t('features.processSteps.selection.description') }
  ];

  const heroNoteTones = ['hero-sticky-amber', 'hero-sticky-blue', 'hero-sticky-green', 'hero-sticky-rose'];
  const heroMetricRotations = ['features-hero-metric-r1', 'features-hero-metric-r2', 'features-hero-metric-r3', 'features-hero-metric-r4'];
  const whyBenefitTones = ['hero-sticky-amber', 'hero-sticky-blue', 'hero-sticky-green', 'hero-sticky-rose'];
  const featureNeedsLayout = [
    { x: '-392px', y: '-410px', rot: '-2.2deg' },
    { x: '144px', y: '-410px', rot: '1.9deg' },
    { x: '-470px', y: '-150px', rot: '-1.4deg' },
    { x: '220px', y: '-150px', rot: '1.2deg' },
    { x: '-360px', y: '170px', rot: '-1.7deg' },
    { x: '-124px', y: '320px', rot: '1.1deg' },
    { x: '112px', y: '170px', rot: '-1deg' }
  ];
  const orbitRef = React.useRef(null);
  const centerCardRef = React.useRef(null);
  const needsCardRefs = React.useRef([]);
  const [needsArrowPaths, setNeedsArrowPaths] = React.useState([]);
  const [orbitViewBox, setOrbitViewBox] = React.useState({ width: 1200, height: 840 });

  const recomputeNeedsArrowPaths = React.useCallback(() => {
    const orbitElement = orbitRef.current;
    const centerElement = centerCardRef.current;
    const cardElements = needsCardRefs.current;

    if (!orbitElement || !centerElement || !cardElements.length) {
      return;
    }

    const orbitRect = orbitElement.getBoundingClientRect();
    if (!orbitRect.width || !orbitRect.height) {
      return;
    }

    const centerRect = centerElement.getBoundingClientRect();
    const centerX = centerRect.left - orbitRect.left + centerRect.width / 2;
    const centerY = centerRect.top - orbitRect.top + centerRect.height / 2;
    const startRadius = Math.min(centerRect.width, centerRect.height) / 2 + 8;

    const nextPaths = cardElements
      .slice(0, features.length)
      .map((cardElement, index) => {
        if (!cardElement) {
          return null;
        }

        const cardRect = cardElement.getBoundingClientRect();
        const targetX = cardRect.left - orbitRect.left + cardRect.width / 2;
        const targetY = cardRect.top - orbitRect.top + cardRect.height / 2;

        const dx = targetX - centerX;
        const dy = targetY - centerY;
        const distance = Math.hypot(dx, dy) || 1;
        const unitX = dx / distance;
        const unitY = dy / distance;

        const startX = centerX + unitX * startRadius;
        const startY = centerY + unitY * startRadius;

        const halfWidth = cardRect.width / 2;
        const halfHeight = cardRect.height / 2;
        const cardPadding = 16;
        const tx = Math.abs(unitX) > 0.0001 ? (halfWidth - cardPadding) / Math.abs(unitX) : Infinity;
        const ty = Math.abs(unitY) > 0.0001 ? (halfHeight - cardPadding) / Math.abs(unitY) : Infinity;
        const edgeDistance = Math.max(0, Math.min(tx, ty));
        const endX = targetX - unitX * edgeDistance;
        const endY = targetY - unitY * edgeDistance;

        const midX = (startX + endX) / 2;
        const midY = (startY + endY) / 2;
        const perpX = -unitY;
        const perpY = unitX;
        const curveDirection = NEEDS_ARROW_CURVE_DIRECTION[index] ?? 0;
        const curveAmount = curveDirection === 0 ? 0 : Math.min(34, Math.max(14, distance * 0.08));
        const controlX = midX + perpX * curveAmount * curveDirection;
        const controlY = midY + perpY * curveAmount * curveDirection;

        return `M ${startX.toFixed(1)} ${startY.toFixed(1)} Q ${controlX.toFixed(1)} ${controlY.toFixed(1)} ${endX.toFixed(1)} ${endY.toFixed(1)}`;
      })
      .filter(Boolean);

    setNeedsArrowPaths(nextPaths);
    setOrbitViewBox((previous) => {
      const next = { width: Math.max(1, Math.round(orbitRect.width)), height: Math.max(1, Math.round(orbitRect.height)) };
      if (next.width === previous.width && next.height === previous.height) {
        return previous;
      }
      return next;
    });
  }, [features.length]);

  React.useEffect(() => {
    const frame = window.requestAnimationFrame(recomputeNeedsArrowPaths);
    window.addEventListener('resize', recomputeNeedsArrowPaths);

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        recomputeNeedsArrowPaths();
      });

      if (orbitRef.current) {
        resizeObserver.observe(orbitRef.current);
      }
      if (centerCardRef.current) {
        resizeObserver.observe(centerCardRef.current);
      }
      needsCardRefs.current.forEach((element) => {
        if (element) {
          resizeObserver.observe(element);
        }
      });
    }

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', recomputeNeedsArrowPaths);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [recomputeNeedsArrowPaths, currentLanguage]);

  return (
    <div className="min-h-screen bg-gray-50 overflow-hidden">
      <section className="relative overflow-hidden public-hero-shell-whiteboard features-hero-whiteboard">
        <div className="hero-whiteboard-grid" aria-hidden="true" />
        <div className="hero-whiteboard-glare" aria-hidden="true" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="grid gap-10 lg:grid-cols-2 items-center"
          >
            <div>
              <span className="inline-flex items-center process-arrow-sticker mb-6">
                {t('features.heroTitle')}
              </span>
              <h1 className="features-hero-heading text-4xl md:text-6xl font-bold mb-6 leading-tight">
                {t('features.heroTitle')}
                <span className="block text-primary-700">{t('features.heroTitleHighlight')}</span>
              </h1>
              <p className="features-hero-subtitle text-xl max-w-xl mb-10">
                {t('features.heroSubtitle')}
              </p>
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <Link to="/signup" className="hero-cta-handwritten inline-flex items-center px-8 py-4 bg-yellow-500 text-gray-900 font-semibold rounded-xl hover:bg-yellow-400 transition-all shadow-lg">
                  {t('navigation.signup')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <Link to="/contact" className="hero-cta-handwritten inline-flex items-center px-8 py-4 border-2 border-slate-300 text-slate-700 rounded-xl hover:bg-white hover:text-primary-700 transition-all font-semibold">
                  {t('navigation.contact')}
                </Link>
              </div>
            </div>

            <div className="features-hero-notes-grid">
              {features.slice(0, 4).map((feature, index) => (
                <article
                  key={feature.title}
                  className={`features-hero-note ${heroNoteTones[index % heroNoteTones.length]} features-hero-note-r${(index % 4) + 1}`}
                >
                  <span className="hero-sticky-pin" />
                  <span className="hero-sticky-fold" />
                  <div className="features-hero-note-head">
                    <div className="features-hero-note-icon-wrap">
                      <feature.icon className="features-hero-note-icon" />
                    </div>
                    <p className="features-hero-note-title">{feature.title}</p>
                  </div>
                  <p className="features-hero-note-body">{feature.description}</p>
                </article>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
          >
            {stats.map((stat, index) => (
              <div
                key={stat.label}
                className={`features-hero-metric-sticker ${heroMetricRotations[index % heroMetricRotations.length]}`}
              >
                <div className="features-hero-metric-number">{stat.number}</div>
                <div className="features-hero-metric-label">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-white border-y border-gray-200 features-needs-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-10">
            <h2 className="features-needs-title text-3xl md:text-4xl font-bold text-gray-900 mb-4">{t('features.needsTitle')}</h2>
            <p className="features-needs-subtitle text-lg text-gray-600 max-w-3xl mx-auto">{t('features.needsSubtitle')}</p>
          </div>

          <div ref={orbitRef} className="features-needs-orbit">
            <div className="features-needs-center">
              <div ref={centerCardRef} className="features-needs-center-logo-card">
                <img src={brandLogo} alt="Candio logo" className="features-needs-center-logo" />
              </div>
              <p className="features-needs-center-label">Candio</p>
            </div>

            <svg
              className="features-needs-arrows"
              viewBox={`0 0 ${orbitViewBox.width} ${orbitViewBox.height}`}
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <defs>
                <marker id="features-needs-arrowhead" markerWidth="11" markerHeight="11" refX="9" refY="5.5" orient="auto">
                  <path d="M1 1 L9 5.5 L1 10" fill="none" stroke="#334155" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </marker>
              </defs>
              {needsArrowPaths.map((path, index) => (
                <path key={`needs-arrow-${index}`} className="features-needs-arrow-main" d={path} markerEnd="url(#features-needs-arrowhead)" />
              ))}
            </svg>

            {features.map((feature, index) => (
              <motion.div
                key={`${feature.title}-${index}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.45, delay: index * 0.04 }}
                className="features-needs-card"
                style={{
                  '--card-x': featureNeedsLayout[index]?.x || '0px',
                  '--card-y': featureNeedsLayout[index]?.y || '0px',
                  '--card-rot': featureNeedsLayout[index]?.rot || '0deg'
                }}
                ref={(element) => {
                  needsCardRefs.current[index] = element;
                }}
              >
                <div className="features-needs-card-head">
                  <div className="features-needs-card-icon-wrap">
                    <feature.icon className="features-needs-card-icon" />
                  </div>
                  <h3 className="features-needs-card-title">{feature.title}</h3>
                </div>
                <p className="features-needs-card-description">{feature.description}</p>
                <div className="space-y-2 features-needs-card-benefits">
                  {feature.benefits.map((benefit) => (
                    <div key={benefit} className="features-needs-card-benefit-row">
                      <span className="features-needs-card-benefit-dot" />
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 features-why-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="inline-flex items-center process-arrow-sticker mb-5">
              {t('features.whyChooseTitle')}
            </span>
            <p className="features-why-subtitle text-lg text-gray-600 max-w-3xl mx-auto">{t('features.whyChooseSubtitle')}</p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: index * 0.05 }}
                viewport={{ once: true }}
                className={`features-why-card ${whyBenefitTones[index % whyBenefitTones.length]} features-why-tilt-${index % 4} features-why-sway-${index % 4}`}
              >
                <span className="hero-sticky-pin" />
                <span className="hero-sticky-fold" />
                <div className="features-why-icon-wrap">
                  <benefit.icon className="features-why-icon" />
                </div>
                <h3 className="features-why-card-title">{benefit.title}</h3>
                <p className="features-why-card-description">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white border-y border-gray-200 features-outcomes-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="features-outcomes-title text-3xl md:text-4xl font-bold text-gray-900 mb-4">{t('features.outcomes.title')}</h2>
            <p className="features-outcomes-subtitle text-lg text-gray-600 max-w-3xl mx-auto">{t('features.outcomes.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {stats.map((stat, index) => (
              <div
                key={`outcome-${stat.label}`}
                className={`features-outcome-sticker features-outcome-sticker-r${(index % 4) + 1}`}
              >
                <div className="features-outcome-number">{stat.number}</div>
                <div className="features-outcome-label mt-2">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8 features-process-section">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="features-process-title text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              {t('features.processTitle')}
            </h2>
            <p className="features-process-subtitle text-xl text-gray-600 max-w-3xl mx-auto">
              {t('features.processSubtitle')}
            </p>
          </motion.div>

          <div className="relative features-process-flow">
            <div className="hidden lg:block features-process-track" aria-hidden="true" />

            <div className="relative z-10 grid gap-8 lg:grid-cols-4">
              {processSteps.map((step, index) => (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className={`text-center features-process-step features-process-step-r${(index % 4) + 1}`}
                >
                  <div className="relative mb-6 features-process-step-head">
                    <div className="features-process-step-number inline-flex items-center justify-center w-16 h-16 text-xl font-bold mx-auto">
                      {index + 1}
                    </div>
                    {index < 3 && (
                      <ArrowRight className="features-process-step-arrow hidden lg:block absolute top-1/2 -right-12 transform -translate-y-1/2 w-6 h-6 text-gray-400" />
                    )}
                  </div>
                  <div className="features-process-step-card">
                  <h3 className="features-process-step-title text-lg font-semibold text-gray-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="features-process-step-description text-gray-600">
                    {step.description}
                  </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white features-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="rounded-3xl features-cta-paper p-8 md:p-10 lg:p-12">
            <Zap className="w-10 h-10 mx-auto mb-4 features-cta-zap" />
            <h2 className="features-cta-title text-3xl md:text-4xl font-bold mb-4">{t('features.ctaTitle')}</h2>
            <p className="features-cta-subtitle text-lg mb-8 max-w-2xl mx-auto">{t('features.ctaSubtitle')}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/signup" className="features-cta-primary px-8 py-4 rounded-xl font-semibold text-lg inline-flex items-center justify-center">
                {t('features.ctaStartNow')}
              </Link>
              <Link to="/contact" className="features-cta-secondary px-8 py-4 rounded-xl font-semibold text-lg inline-flex items-center justify-center">
                {t('features.ctaContact')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default FeaturesPage;
